import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import SideMenu from "../shared/navigation/SideMenu/SideMenu";
import SideNav from "../shared/navigation/SideNav/SideNav";
import { SideNavTitleSlotProvider } from "../shared/navigation/SideNav/SideNavTitleSlot";
import TenantNoticeBanner from "../shared/components/TenantNoticeBanner";
import MaintenanceBanner from "../shared/components/MaintenanceBanner";
import MaintenanceBlock from "../kontrolEdge/views/MaintenanceBlock";
import { useMaintenanceBlock } from "@shared/hooks/useMaintenanceStatus";
import ArchiFab from "../archi/ArchiFab";
import ArchiSidebar from "../archi/ArchiSidebar";
import { PageHeaderActionsProvider } from "../shared/navigation/PageHeaderActions";
/* Carries the What's new walkthrough (CTX-510): the launch starts in the
   SideMenu footer card and the tour runs on a screen inside `<Outlet />`, so
   the state belongs above both. The boundary owns its own changelog gate. */
import { WalkthroughTourBoundary } from "../shared/tour/WalkthroughTourProvider";
/* Carries the What's new dialog (CTX-592) for the same reason, and nested
   inside the tour's boundary because its "Walk me through it" launches one.
   The rail cannot hold it: `SideMenuBody` swaps its whole tree when the profile
   finishes loading, which unmounted the dialog a second after it opened. */
import { WhatsNewBoundary } from "@shared/changelog/WhatsNewBoundary";
/* Open views (project 13D): the provider owns the gestures (right click "Add view",
   Cmd/Ctrl+click, middle click) for the rail and the page alike, so it wraps both. It
   sits inside the tour and What's new boundaries (intent §3). The views themselves
   render in SideNav's title row. */
import { ViewsProvider } from "@shared/navigation/tabs/ViewsProvider";

interface AppLayoutProps {
	removePadding?: boolean;
}

/**
 * The two changelog surfaces whose state has to outlive the app loading, in the
 * order they depend on each other: the What's new dialog launches a walkthrough
 * tour, so it sits inside the tour's boundary.
 *
 * Composed here rather than nested inline so the shell below keeps its
 * indentation — and so the pair reads as one decision, which is what it is:
 * both live at this level because everything under them gets rebuilt when the
 * profile lands, and neither surface may die with that rebuild.
 */
const ChangelogChrome: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<WalkthroughTourBoundary>
		<WhatsNewBoundary>{children}</WhatsNewBoundary>
	</WalkthroughTourBoundary>
);

export default function AppLayout({ removePadding = false }: AppLayoutProps) {
	const location = useLocation();

	/* A blocking window replaces the page rather than redirecting: the side
	   menu stays put, so Support is one click away and the customer can see
	   they are still signed in and still have an account. A redirect would
	   read as being thrown out. */
	const blockingWindow = useMaintenanceBlock();
	
	// Insight Topology V2 (CTX-112) is a full-height canvas view: it needs the
	// Outlet wrapper itself to be a definite-height flex column (flex-1 +
	// min-h-0) so the canvas below its compact header can resolve `h-full`
	// and fill the remaining viewport instead of relying on a `vh` guess.
	// Same view now also mounts at /reflector-v2/network-topology (CTX-97
	// product launch inside Reflector V2) — both mounts need the treatment.
	const isInsightTopologyV2Route =
		location.pathname.startsWith('/insight/topology-v2') || location.pathname.startsWith('/reflector-v2/network-topology');

	// Check if current route should have no padding
	const shouldRemovePadding = removePadding || location.pathname.includes('/bgp-visibility') || isInsightTopologyV2Route;

	// Allow full-width layout for detail pages with wide content (e.g. topology diagrams).
	// BGP Visibility is a wide topology + docked-detail-panel view; the 1600px cap crops it
	// and centers it (leaving gutters) on monitor-width screens.
	const shouldRemoveMaxWidth =
		location.pathname.includes('/kontrol/backbone-details') ||
		location.pathname.includes('/bgp-visibility') ||
		isInsightTopologyV2Route;

	// Hide TenantNoticeBanner on Resource Manager routes
	const isResourceManagerRoute = location.pathname.includes('/kontrol/admin-center/');

	return (
		<ChangelogChrome>
			<ViewsProvider>
			<div className="flex h-screen bg-semantic-bg-secondary">
				<SideMenu />

				{/* Wraps SideNav *and* the Outlet so a page can render controls into the
				    title row beside the page title (see SideNavTitleSlot). PageHeaderActionsProvider
				    (CTX-112) nests inside for the same reason — a page (e.g. Insight Topology V2)
				    renders its compact toolbar into the header from within the Outlet. */}
				<SideNavTitleSlotProvider>
					<div className="relative flex flex-col flex-1 min-w-0 bg-[var(--semantic-bg-primary)]">
						<PageHeaderActionsProvider>
							<SideNav />
							{/* The maintenance notice ignores the Resource Manager exclusion
							    below on purpose: that rule was written for per-tenant
							    notices, and Admin Center hosts views that read the same
							    telemetry. Hiding the explanation from internal users is
							    exactly backwards. */}
							{!blockingWindow && <MaintenanceBanner />}
							{!isResourceManagerRoute && <TenantNoticeBanner />}
							<div
								className={`main-container-global mx-auto w-full ${shouldRemoveMaxWidth ? '' : 'max-w-[1600px]'} ${shouldRemovePadding ? 'no-padding' : ''} ${isInsightTopologyV2Route ? 'flex flex-1 flex-col min-h-0 overflow-hidden' : ''}`}
							>
								{blockingWindow ? (
									<MaintenanceBlock window={blockingWindow} />
								) : (
									<Outlet />
								)}
							</div>
						</PageHeaderActionsProvider>

						{/* Archi FAB lives inside the main content column so it
						    sits at the bottom-left of the dashboard area, never
						    overlapping the SideMenu nav links. */}
						<ArchiFab />
					</div>
				</SideNavTitleSlotProvider>

				<ArchiSidebar />
			</div>
			</ViewsProvider>
		</ChangelogChrome>
	);
}
